import React, { useState, useEffect } from 'react';
import bulgarianLabels from '../utils/bulgarianLabels.json';
import './ExcelValidation.css';

const ExcelValidation = ({ results, onConfirm, onCancel }) => {
  const [editableData, setEditableData] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');

  // Define columns in order of importance - moved outside to prevent re-creation
  const columns = React.useMemo(() => [
    { key: 'fileName', label: 'Файл / File', width: '150px', type: 'text' },
    { key: 'invoiceNumber', label: bulgarianLabels.invoiceNumber, width: '120px', type: 'text', required: true },
    { key: 'invoiceDate', label: bulgarianLabels.invoiceDate, width: '120px', type: 'date', required: true },
    { key: 'dueDate', label: bulgarianLabels.dueDate, width: '120px', type: 'date' },
    { key: 'supplierName', label: bulgarianLabels.supplierName, width: '180px', type: 'text', required: true },
    { key: 'supplierVatNumber', label: bulgarianLabels.supplierVatNumber, width: '140px', type: 'text', required: true },
    { key: 'customerName', label: bulgarianLabels.customerName, width: '180px', type: 'text' },
    { key: 'customerVatNumber', label: bulgarianLabels.customerVatNumber, width: '140px', type: 'text' },
    { key: 'subtotalAmount', label: bulgarianLabels.subtotalAmount, width: '120px', type: 'number' },
    { key: 'vatAmount', label: bulgarianLabels.vatAmount, width: '120px', type: 'number' },
    { key: 'vatRate', label: bulgarianLabels.vatRate, width: '100px', type: 'text' },
    { key: 'totalAmount', label: bulgarianLabels.totalAmount, width: '120px', type: 'number', required: true },
    { key: 'currency', label: bulgarianLabels.currency, width: '80px', type: 'text', required: true },
    { key: 'paymentMethod', label: bulgarianLabels.paymentMethod, width: '120px', type: 'text' },
    { key: 'description', label: bulgarianLabels.description, width: '200px', type: 'text' }
  ], []);

  const requiredFields = React.useMemo(() => 
    columns.filter(col => col.required).map(col => col.key), 
    [columns]
  );

  const validateAllInvoices = React.useCallback((invoices) => {
    const errors = {};
    
    invoices.forEach((invoice, index) => {
      const invoiceErrors = {};
      
      requiredFields.forEach(field => {
        if (!invoice[field] || String(invoice[field]).trim() === '') {
          invoiceErrors[field] = 'Задължително поле / Required field';
        }
      });

      // Date validation
      if (invoice.invoiceDate && !isValidDate(invoice.invoiceDate)) {
        invoiceErrors.invoiceDate = 'Невалидна дата / Invalid date';
      }
      if (invoice.dueDate && !isValidDate(invoice.dueDate)) {
        invoiceErrors.dueDate = 'Невалидна дата / Invalid date';
      }

      // Amount validation
      ['subtotalAmount', 'vatAmount', 'totalAmount'].forEach(field => {
        const value = invoice[field];
        if (value !== null && value !== undefined && value !== '' && isNaN(parseFloat(value))) {
          invoiceErrors[field] = 'Невалидна сума / Invalid amount';
        }
      });

      // VAT validation - ensure vatRate is a string before using match
      if (invoice.vatRate && typeof invoice.vatRate === 'string' && !invoice.vatRate.match(/^\d+(\.\d+)?%?$/)) {
        invoiceErrors.vatRate = 'Невалиден ДДС процент / Invalid VAT rate';
      } else if (invoice.vatRate && typeof invoice.vatRate !== 'string' && invoice.vatRate !== null && invoice.vatRate !== undefined) {
        // Convert non-string vatRate to string and validate
        const vatRateStr = String(invoice.vatRate);
        if (!vatRateStr.match(/^\d+(\.\d+)?%?$/)) {
          invoiceErrors.vatRate = 'Невалиден ДДС процент / Invalid VAT rate';
        }
      }

      if (Object.keys(invoiceErrors).length > 0) {
        errors[invoice._id] = invoiceErrors;
      }
    });

    setValidationErrors(errors);
  }, [requiredFields]); // Include requiredFields in dependencies

  const isValidDate = (dateString) => {
    if (!dateString) return false;
    return !isNaN(Date.parse(String(dateString)));
  };

  useEffect(() => {
    if (results?.data) {
      let invoicesData = [];
      
      if (results.type === 'multi-invoice' && results.data.invoices) {
        // Handle multi-invoice data
        invoicesData = results.data.invoices.map((invoice, index) => ({
          ...invoice.extractedFields,
          _id: `invoice-${index}`,
          fileName: invoice.fileName || `Invoice ${index + 1}`,
          _originalIndex: index
        }));
      } else if (results.type === 'single-invoice' && results.data.extractedFields) {
        // Handle single invoice data
        invoicesData = [{
          ...results.data.extractedFields,
          _id: 'invoice-0',
          fileName: results.data.extractedFields.fileName || 'Single Invoice',
          _originalIndex: 0
        }];
      } else if (results.type === 'multi-file' && Array.isArray(results.data)) {
        // Handle multi-file upload data
        invoicesData = results.data.map((invoice, index) => ({
          ...invoice,
          _id: `file-${index}`,
          fileName: invoice._fileName || invoice.fileName || `File ${index + 1}`,
          _originalIndex: index
        }));
      }

      // Clean and normalize the data
      const cleanedData = invoicesData.map(invoice => ({
        ...invoice,
        // Ensure all fields are properly typed
        vatRate: invoice.vatRate ? String(invoice.vatRate) : '',
        subtotalAmount: invoice.subtotalAmount ? String(invoice.subtotalAmount) : '',
        vatAmount: invoice.vatAmount ? String(invoice.vatAmount) : '',
        totalAmount: invoice.totalAmount ? String(invoice.totalAmount) : '',
        invoiceDate: invoice.invoiceDate ? String(invoice.invoiceDate) : '',
        dueDate: invoice.dueDate ? String(invoice.dueDate) : ''
      }));

      setEditableData(cleanedData);
      validateAllInvoices(cleanedData);
    }
  }, [results, validateAllInvoices]);

  // Separate useEffect to handle validation when data changes
  useEffect(() => {
    if (editableData.length > 0) {
      validateAllInvoices(editableData);
    }
  }, [editableData, validateAllInvoices]);

  const handleCellChange = React.useCallback((invoiceId, field, value) => {
    console.log('📝 Cell change:', { invoiceId, field, value });
    
    setEditableData(prev => {
      const updated = prev.map(invoice => 
        invoice._id === invoiceId 
          ? { ...invoice, [field]: value }
          : invoice
      );
      
      console.log('📝 Updated data:', updated);
      return updated;
    });
  }, []);

  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const getSortedData = () => {
    let sortedData = [...editableData];

    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (sortConfig.direction === 'asc') {
          return aValue.toString().localeCompare(bValue.toString(), 'bg', { numeric: true });
        } else {
          return bValue.toString().localeCompare(aValue.toString(), 'bg', { numeric: true });
        }
      });
    }

    // Apply filter
    if (filterText) {
      sortedData = sortedData.filter(invoice =>
        Object.values(invoice).some(value =>
          value && value.toString().toLowerCase().includes(filterText.toLowerCase())
        )
      );
    }

    return sortedData;
  };

  const handleConfirm = async () => {
    // Final validation
    validateAllInvoices(editableData);
    
    if (Object.keys(validationErrors).length > 0) {
      alert('Моля поправете грешките преди продължаване / Please fix errors before continuing');
      return;
    }

    setIsProcessing(true);
    
    try {
      await onConfirm(editableData);
    } catch (error) {
      console.error('Validation confirmation error:', error);
      alert('Грешка при потвърждаване / Confirmation error');
    } finally {
      setIsProcessing(false);
    }
  };

  const getCellError = (invoiceId, field) => {
    return validationErrors[invoiceId]?.[field];
  };

  const hasAnyErrors = () => {
    return Object.keys(validationErrors).length > 0;
  };

  const getErrorCount = () => {
    return Object.values(validationErrors).reduce((total, errors) => total + Object.keys(errors).length, 0);
  };

  const renderCell = (invoice, column) => {
    const value = invoice[column.key] || '';
    const error = getCellError(invoice._id, column.key);
    const isRequired = column.required;

    return (
      <td key={`${invoice._id}-${column.key}`} className={`excel-cell ${error ? 'error' : ''}`}>
        {column.key === 'fileName' ? (
          <div className="file-name-cell" title={value}>
            📄 {value}
          </div>
        ) : (
          <input
            type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : 'text'}
            value={value}
            onChange={(e) => handleCellChange(invoice._id, column.key, e.target.value)}
            className={`excel-input ${isRequired ? 'required' : ''}`}
            placeholder={isRequired ? 'Задължително' : ''}
            step={column.type === 'number' ? '0.01' : undefined}
          />
        )}
        {error && <div className="cell-error" title={error}>⚠️</div>}
      </td>
    );
  };

  const sortedData = getSortedData();

  return (
    <div className="excel-validation-container">
      <div className="validation-header">
        <h2>📊 Валидация на данни / Data Validation</h2>
        <div className="validation-summary">
          <span className="invoice-count">
            📄 {editableData.length} фактури / invoices
          </span>
          {hasAnyErrors() && (
            <span className="error-count">
              ⚠️ {getErrorCount()} грешки / errors
            </span>
          )}
        </div>
      </div>

      <div className="table-controls">
        <div className="filter-controls">
          <input
            type="text"
            placeholder="🔍 Търсене в таблицата / Search table..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="filter-input"
          />
          {filterText && (
            <button onClick={() => setFilterText('')} className="clear-filter">
              ❌
            </button>
          )}
        </div>
        
        <div className="table-info">
          Показани: {sortedData.length} от {editableData.length}
        </div>
      </div>

      <div className="excel-table-wrapper">
        <table className="excel-table">
          <thead>
            <tr>
              {columns.map(column => (
                <th 
                  key={column.key}
                  style={{ width: column.width }}
                  className={`sortable ${sortConfig.key === column.key ? sortConfig.direction : ''}`}
                  onClick={() => handleSort(column.key)}
                >
                  <div className="header-content">
                    <span className="header-text">{column.label}</span>
                    {column.required && <span className="required-star">*</span>}
                    <span className="sort-icon">
                      {sortConfig.key === column.key ? 
                        (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕️'
                      }
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map(invoice => (
              <tr key={invoice._id} className={validationErrors[invoice._id] ? 'has-errors' : ''}>
                {columns.map(column => renderCell(invoice, column))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="validation-actions">
        <button 
          onClick={onCancel}
          className="cancel-btn"
          disabled={isProcessing}
        >
          ❌ Отказ / Cancel
        </button>
        
        <button 
          onClick={handleConfirm}
          className={`confirm-btn ${hasAnyErrors() ? 'has-errors' : ''}`}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>⏳ Обработване...</>
          ) : hasAnyErrors() ? (
            <>⚠️ Потвърди с грешки ({getErrorCount()})</>
          ) : (
            <>✅ Потвърди всички данни</>
          )}
        </button>
      </div>
    </div>
  );
};

export default ExcelValidation;
